import { SectionNotes } from 'services/api/vehicle-sections/types';

import styled from 'styled-components';
import GptIcon from 'components/Icons/gpt.svg';
import { useState } from 'react';
import { useScreen } from 'usehooks-ts';
import { useAiDescription } from 'hooks/useAiDescription';
import { useTireMessage } from 'hooks/useTireMessage';

const NoteWrapper = styled.div<{
  expanded: boolean;
  maxHeight: number;
}>`
  position: absolute;
  left: 50%;
  padding: 12px;
  gap: 8px;
  transform: translateX(-50%);
  bottom: ${({ expanded }) => (expanded ? 'auto' : '0')};
  top: ${({ expanded }) => (expanded ? '0' : 'auto')};
  height: ${({ expanded }) => (expanded ? '100%' : 'auto')};

  width: 100%;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.4) -8.62%, rgba(102, 102, 102, 0.4) 131.9%);

  display: flex;
  flex-direction: column;
  color: #fff;
  cursor: pointer;
  transition: top 0.3s ease, bottom 0.3s ease;

  @media (min-width: 768px) {
    bottom: 0;
    top: auto;
    height: auto;
  }
`;

const HeaderWrapper = styled.div<{ expanded: boolean }>`
  display: flex;
  gap: 8px;
  flex-direction: column;
`;

const Header = styled.div<{ expanded: boolean }>`
  min-height: 32px;

  box-sizing: border-box;
  font-size: 14px;
  line-height: 100%;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

  text-align: justify;
  text-justify: inter-word;

  overflow: hidden;
  white-space: normal;
  word-break: break-word;

  ${({ expanded }) =>
    !expanded &&
    `
    -webkit-line-clamp: 2; 
    -webkit-box-orient: vertical;
  `}
`;

const ContentWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 8px 18px;

  @media (min-width: 768px) {
    flex: unset;
  }
`;

const Content = styled.div`
  font-size: 14px;
  line-height: 100%;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  text-align: justify;
  text-justify: inter-word;
`;

const HeaderContentWrapper = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
`;

export const FullScreenDescription = ({ notes, type }: { notes?: SectionNotes; type?: string }) => {
  const { width = 0 } = useScreen();
  const isMobile = width < 768;
  const [isExpanded, setIsExpanded] = useState(!isMobile);

  const { isVisible, value: aiDescription } = useAiDescription(notes, type);
  const message = useTireMessage(notes, type);

  if (!message) return null;
  if (!isVisible) return <span className="image-gallery-description">{message}</span>;

  const toggleExpanded = () => {
    if (!isMobile) return;
    return setIsExpanded(curr => !curr);
  };

  return (
    <NoteWrapper expanded={isExpanded} onClick={toggleExpanded}>
      <HeaderWrapper expanded={isExpanded}>
        {notes?.message && type?.endsWith('_tire') && (
          <HeaderContentWrapper>
            <Header expanded={isExpanded}>{notes?.message}</Header>
          </HeaderContentWrapper>
        )}
        <HeaderContentWrapper>
          <div style={{ display: 'flex' }}>
            <GptIcon />
          </div>
          <Header expanded={isExpanded}>{message}</Header>
        </HeaderContentWrapper>
      </HeaderWrapper>
      {isExpanded && (
        <ContentWrapper>
          <Content>{aiDescription}</Content>
        </ContentWrapper>
      )}
    </NoteWrapper>
  );
};
